/**
 * Type Selection Sheet — bottom sheet for choosing challenge type (private/public/competition).
 * Handles permission checks and premium/expert gating.
 */

import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Crown, Building2 } from 'lucide-react'
import type { NavigateFunction } from 'react-router-dom'

interface TypeSelectionSheetProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (type: 'private' | 'public' | 'competition') => void
  isPremium: boolean
  canCreateAny: boolean
  navigate: NavigateFunction
}

export function TypeSelectionSheet({
  isOpen,
  onClose,
  onSelect,
  isPremium,
  canCreateAny,
  navigate,
}: TypeSelectionSheetProps): React.ReactElement | null {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-[32px] max-w-md mx-auto px-6 pb-safe pt-4"
          >
            <div className="w-10 h-1 bg-white/[0.15] rounded-full mx-auto mb-5" />
            <h3 className="text-lg font-display font-bold text-text mb-4">
              {t('challenges.chooseType')}
            </h3>

            <div className="space-y-2.5 mb-6">
              <TypeButton
                icon="🔒"
                title={t('challenges.types.private')}
                desc={!isPremium ? t('challenges.premiumRequired') : t('challenges.types.privateDesc')}
                onClick={() => onSelect('private')}
                disabled={!isPremium}
                locked={!isPremium}
              />
              <TypeButton
                icon="🌍"
                title={t('challenges.types.public')}
                desc={!isPremium ? t('challenges.premiumRequired') : canCreateAny ? t('challenges.types.publicDesc') : t('challenges.expertOnly')}
                onClick={() => onSelect('public')}
                disabled={!canCreateAny}
                locked={!canCreateAny}
              />
              <TypeButton
                icon="🏆"
                title={t('challenges.types.competition')}
                desc={!isPremium ? t('challenges.premiumRequired') : canCreateAny ? t('challenges.types.competitionDesc') : t('challenges.expertOnly')}
                onClick={() => onSelect('competition')}
                disabled={!canCreateAny}
                locked={!canCreateAny}
              />
            </div>

            {!isPremium && (
              <button
                onClick={() => { onClose(); navigate('/profile', { state: { openPremium: true } }) }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 mb-4 tap-highlight-transparent active:scale-[0.98] transition-transform"
              >
                <Crown size={18} className="text-primary" />
                <span className="text-[14px] font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  {t('challenges.upgradeToPremium')}
                </span>
              </button>
            )}

            {isPremium && !canCreateAny && (
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 mb-4">
                <p className="text-[12px] text-text-hint mb-3">{t('challenges.expertOnlyHint')}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { onClose(); navigate('/profile', { state: { openVerification: 'expert' } }) }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary text-[12px] font-semibold tap-highlight-transparent"
                  >
                    <Crown size={14} />{t('challenges.becomeExpert')}
                  </button>
                  <button
                    onClick={() => { onClose(); navigate('/profile', { state: { openVerification: 'company' } }) }}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-text-hint text-[12px] font-semibold tap-highlight-transparent"
                  >
                    <Building2 size={14} />{t('challenges.becomeCompany')}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ── Type Button ─────────────────────────────────────────────

function TypeButton({
  icon,
  title,
  desc,
  onClick,
  disabled,
  locked,
}: {
  icon: string
  title: string
  desc: string
  onClick: () => void
  disabled: boolean
  locked?: boolean
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors tap-highlight-transparent ${
        disabled
          ? 'border-white/[0.04] bg-white/[0.01] opacity-50'
          : 'border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.05] active:bg-white/[0.07]'
      }`}
    >
      <span className="text-2xl shrink-0">{icon}</span>
      <div className="flex-1 text-left min-w-0">
        <p className="text-[14px] font-semibold text-text">{title}</p>
        <p className="text-[11px] text-text-hint truncate">{desc}</p>
      </div>
      {locked && <span className="text-[10px] text-warning">🔒</span>}
    </button>
  )
}
